import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Breadcrumb,
  Button,
  Loader,
} from "@relentless/rmds/v1/relentless.esm.js";
import { SectionHeader } from "../components/SiteUI";
import ShareThisArticle from "../components/ShareThisArticle";
import { getBlogPostBySlug, useBlogPosts } from "../lib/api";
import { REVEAL_ANIMATIONS, useArticleBodyReveal } from "../lib/animations";
import { MEDIA_BASE_URL } from "../config/apiConfig";
import { withPublicUrl } from "../lib/publicUrl";
import styles from "./BlogPostPage.module.scss";

const INTERNAL_HOSTS = new Set([
  "therelentlessminds.com",
  "www.therelentlessminds.com",
  "relentlessminds.com",
  "www.relentlessminds.com",
]);

const normalizeInternalLinksInHtml = (html = "") =>
  String(html || "").replace(
    /(<a\b[^>]*?\shref=)(["'])(.*?)\2/gi,
    (match, prefix, quote, href) => {
      if (!href || typeof href !== "string") return match;

      try {
        const parsed = new URL(href);
        if (!INTERNAL_HOSTS.has(parsed.hostname.toLowerCase())) return match;
        if (!parsed.pathname.startsWith("/blog")) return match;
        return `${prefix}${quote}${parsed.pathname}${parsed.search}${parsed.hash}${quote}`;
      } catch (_) {
        return match;
      }
    },
  );

export default function BlogPostPage({ postId }) {
  // postId is slug (or id) from hash route
  const { posts: list, loading } = useBlogPosts();
  const [fallbackPost, setFallbackPost] = useState(null);
  const articleBodyRef = useRef(null);

  const toMediaUrl = (url) => {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("//")) return `${window.location.protocol}${url}`;
    const mediaBase = MEDIA_BASE_URL || "";
    if (mediaBase && url.startsWith("/")) {
      return `${mediaBase.replace(/\/+$/, "")}${url}`;
    }
    return url;
  };

  const post = useMemo(
    () =>
      (list || []).find(
        (p) => p.slug === postId || String(p.id) === String(postId),
      ) || null,
    [list, postId],
  );

  useEffect(() => {
    let cancelled = false;

    const fetchFallback = async () => {
      if (!postId || post) {
        setFallbackPost(null);
        return;
      }

      const found = await getBlogPostBySlug(postId);
      if (!cancelled) {
        setFallbackPost(found || null);
      }
    };

    fetchFallback();

    return () => {
      cancelled = true;
    };
  }, [postId, post]);

  const activePost = post || fallbackPost;

  const index = useMemo(() => {
    if (!post) return -1;
    return (list || []).findIndex((p) => p.slug === post.slug);
  }, [list, post]);

  const prev = index > 0 ? list[index - 1] : null;
  const next =
    index >= 0 && index < (list?.length || 0) - 1 ? list[index + 1] : null;

  const extractTextFromBody = useCallback((body) => {
    if (!body) return "";
    if (typeof body === "string") return body;
    if (Array.isArray(body)) {
      return body.map((node) => extractTextFromBody(node)).join(" ");
    }
    if (typeof body === "object") {
      if (Array.isArray(body.children)) {
        return body.children
          .map((child) => extractTextFromBody(child))
          .join(" ");
      }
      if (typeof body.text === "string") return body.text;
    }
    return "";
  }, []);

  const meta = useMemo(() => {
    if (!activePost) return { eyebrow: "", metaText: "" };
    const eyebrow = activePost.cat || "";
    const textOnly = extractTextFromBody(activePost.body).replace(
      /<[^>]+>/g,
      " ",
    );
    const words = textOnly.trim().split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.round(words / 200));
    const date = activePost.published || "";
    const metaText = date ? `${date} • ${mins} min read` : `${mins} min read`;
    return { eyebrow, metaText };
  }, [activePost, extractTextFromBody]);

  const applyInlineFormatting = useCallback(
    (text) =>
      text
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(
          /\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
        ),
    [],
  );

  const markdownToHtml = useCallback(
    (markdown) => {
      if (!markdown) return "";
      const lines = markdown.replace(/\r\n/g, "\n").split("\n");
      const html = [];
      let listType = null; // "ul" | "ol"
      let tableLines = [];
      let paragraphLines = [];
      let sectionOpen = false;

      const closeList = () => {
        if (listType) {
          html.push(`</${listType}>`);
          listType = null;
        }
      };

      const closeSection = () => {
        if (sectionOpen) {
          html.push("</div>");
          sectionOpen = false;
        }
      };

      const closeParagraph = () => {
        if (!paragraphLines.length) return;
        const text = paragraphLines.join(" ").trim();
        if (text) {
          html.push(`<p>${applyInlineFormatting(text)}</p>`);
        }
        paragraphLines = [];
      };

      const closeTable = () => {
        if (!tableLines.length) return;
        const rows = tableLines.map((row) =>
          row
            .trim()
            .slice(1, -1)
            .split("|")
            .map((cell) => applyInlineFormatting(cell.trim())),
        );
        const header = rows[0] || [];
        const bodyRows = rows.slice(2);
        const headerHtml = header
          .map((cell) => `<th>${cell || ""}</th>`)
          .join("");
        const bodyHtml = bodyRows
          .map(
            (cells) => `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`,
          )
          .join("");
        html.push(
          `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`,
        );
        tableLines = [];
        paragraphLines = [];
      };

      lines.forEach((line) => {
        const trimmed = line.trim();

        if (/^\s*\|.*\|\s*$/.test(line)) {
          closeParagraph();
          closeList();
          tableLines.push(line);
          return;
        }

        closeTable();

        if (!trimmed) {
          closeList();
          closeParagraph();
          return;
        }

        const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
          closeList();
          closeParagraph();
          const level = headingMatch[1].length;
          if (level === 2) {
            closeSection();
            sectionOpen = true;
            html.push(`<div class="articleSection">`);
          }
          html.push(
            `<h${level}>${applyInlineFormatting(headingMatch[2])}</h${level}>`,
          );
          return;
        }

        const olMatch = trimmed.match(/^\d+\.\s+(.*)$/);
        if (olMatch) {
          closeParagraph();
          if (listType !== "ol") {
            closeList();
            listType = "ol";
            html.push("<ol>");
          }
          html.push(`<li>${applyInlineFormatting(olMatch[1])}</li>`);
          return;
        }

        const ulMatch = trimmed.match(/^[-*]\s+(.*)$/);
        if (ulMatch) {
          closeParagraph();
          if (listType !== "ul") {
            closeList();
            listType = "ul";
            html.push("<ul>");
          }
          html.push(`<li>${applyInlineFormatting(ulMatch[1])}</li>`);
          return;
        }

        closeList();
        paragraphLines.push(trimmed);
      });

      closeList();
      closeTable();
      closeParagraph();
      closeSection();
      return html.join("");
    },
    [applyInlineFormatting],
  );

  const renderRichHtml = useCallback(
    (nodes) => {
      if (!nodes) return "";
      if (typeof nodes === "string") {
        const trimmed = nodes.trim();
        const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(trimmed);
        return looksLikeHtml ? trimmed : markdownToHtml(nodes);
      }
      if (Array.isArray(nodes)) {
        return nodes.map((n) => renderRichHtml(n)).join("");
      }
      if (typeof nodes.text === "string") {
        return nodes.text;
      }

      const renderChildren = (children) => {
        if (!children) return "";
        if (Array.isArray(children)) {
          return children.map((child) => renderRichHtml(child)).join("");
        }
        return renderRichHtml(children);
      };

      switch (nodes.type) {
        case "heading": {
          const level = nodes.level || 2;
          const tag = `h${Math.min(Math.max(level, 1), 6)}`;
          return `<${tag}>${renderChildren(nodes.children)}</${tag}>`;
        }
        case "paragraph":
          return `<p>${renderChildren(nodes.children)}</p>`;
        case "list-item":
          return `<li>${renderChildren(nodes.children)}</li>`;
        case "list": {
          const tag = nodes.format === "unordered" ? "ul" : "ol";
          const items =
            Array.isArray(nodes.children) &&
            nodes.children
              .map((child) => {
                // Preserve nested list types (e.g., ol > ul)
                if (child.type === "list") {
                  return `<li>${renderRichHtml(child)}</li>`;
                }
                if (child.type === "list-item") {
                  return renderRichHtml(child);
                }
                return `<li>${renderChildren(child.children || child)}</li>`;
              })
              .join("");
          return `<${tag}>${items || ""}</${tag}>`;
        }
        default:
          return renderChildren(nodes.children);
      }
    },
    [markdownToHtml],
  );

  const blocksHtml = useMemo(() => {
    const blocks = Array.isArray(activePost?.blocks) ? activePost.blocks : [];
    if (!blocks.length) return "";

    const renderBlock = (block) => {
      if (!block) return "";
      const comp = block.__component || "";
      if (comp.includes("rich-text")) {
        const html = renderRichHtml(block.content || block.body || "");
        return `<div class="cms-block cms-rich-text">${html}</div>`;
      }
      if (comp.includes("section-header")) {
        const heading = block.heading || block.title || "";
        const sub = block.subheading || block.subtitle || "";
        return `<section class="cms-block cms-section-header"><h2>${heading}</h2>${
          sub ? `<p>${sub}</p>` : ""
        }</section>`;
      }
      return "";
    };

    return blocks.map(renderBlock).filter(Boolean).join("");
  }, [activePost?.blocks, renderRichHtml]);

  const bodyHtml = useMemo(() => {
    if (blocksHtml) return blocksHtml;
    if (!activePost?.body) return "";
    return renderRichHtml(activePost.body);
  }, [activePost?.body, blocksHtml, renderRichHtml]);

  const bodyHtmlWithAbsoluteImages = useMemo(() => {
    if (!bodyHtml) return "";
    if (typeof window === "undefined") return bodyHtml;

    const mediaBase = MEDIA_BASE_URL.replace(/\/+$/, "");

    const resolveBodyImageSrc = (src) => {
      if (!src) return src;
      if (/^(https?:|data:|blob:)/i.test(src)) {
        return src;
      }
      if (src.startsWith("//")) {
        return `${window.location.protocol}${src}`;
      }
      if (src.startsWith("/")) {
        return mediaBase ? `${mediaBase}${src}` : withPublicUrl(src);
      }
      const cleaned = src.replace(/^\.?\//, "");
      return mediaBase ? `${mediaBase}/${cleaned}` : withPublicUrl(`/${cleaned}`);
    };

    const styledHtml = bodyHtml
      .replace(
        /<img\s+([^>]*?)src=(["'])(.*?)\2/gi,
        (match, pre, quote, src) => {
          const resolved = resolveBodyImageSrc(src.trim());
          if (!resolved || resolved === src) return match;
          return `<img ${pre}src=${quote}${resolved}${quote}`;
        },
      )
      .replace(/<p(?![^>]*class=)([^>]*)>/gi, '<p class="t-body"$1>')
      .replace(/<h2(?![^>]*class=)([^>]*)>/gi, '<h2 class="t-h2"$1>')
      .replace(/<h3(?![^>]*class=)([^>]*)>/gi, '<h3 class="t-h3"$1>')
      .replace(/<h4(?![^>]*class=)([^>]*)>/gi, '<h4 class="t-h4"$1>')
      .replace(
        /<blockquote(?![^>]*class=)([^>]*)>/gi,
        '<blockquote class="t-subtitle"$1>',
      )
      .replace(/<img\s+([^>]*?)(\/?)>/gi, (match, attrs, slash) => {
        const withClass = /class\s*=/.test(attrs)
          ? attrs.replace(
              /class=(["'])(.*?)\1/i,
              (classMatch, quote, value) =>
                `class=${quote}${value} article-media${quote}`,
            )
          : `class="article-media" ${attrs}`;
        if (/data-animation\s*=/.test(withClass)) {
          return `<img ${withClass}${slash ? " /" : ""}>`;
        }
        const trailing = slash ? " /" : "";
        return `<img ${withClass} data-animation="${REVEAL_ANIMATIONS.zoomIn}"${trailing}>`;
      });
    return normalizeInternalLinksInHtml(styledHtml);
  }, [bodyHtml]);

  const referencesHtml = useMemo(() => {
    const refs = activePost?.references;
    if (!refs || (Array.isArray(refs) && refs.length === 0)) return "";
    const renderList = (items) =>
      `<ol>${items
        .map((r) => `<li>${normalizeInternalLinksInHtml(renderRichHtml(r))}</li>`)
        .join("")}</ol>`;

    if (Array.isArray(refs)) {
      return renderList(refs);
    }

    if (typeof refs === "string") {
      // Treat plain text as newline/semicolon separated list items
      const items = refs
        .split(/\r?\n|;/)
        .map((s) => s.trim())
        .filter(Boolean);
      return items.length ? renderList(items) : `<p>${normalizeInternalLinksInHtml(refs)}</p>`;
    }

    return renderList([refs]);
  }, [activePost?.references, renderRichHtml]);

  useArticleBodyReveal(styles.articleBody, [bodyHtml]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaRoot = articleBodyRef.current;
    if (!mediaRoot) return undefined;

    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;

    if (prefersReducedMotion) {
      mediaRoot.querySelectorAll("img").forEach((img) => {
        img.style.setProperty("--article-image-shift", "0px");
      });
      return undefined;
    }

    let frame = 0;

    const updateParallax = () => {
      frame = 0;
      const viewportHeight = window.innerHeight || 0;
      mediaRoot.querySelectorAll("img").forEach((img) => {
        const rect = img.getBoundingClientRect();
        const centerOffset = rect.top + rect.height / 2 - viewportHeight / 2;
        const shift = Math.max(-48, Math.min(48, centerOffset * -0.08));
        img.style.setProperty("--article-image-shift", `${shift.toFixed(2)}px`);
      });
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateParallax);
    };

    updateParallax();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [bodyHtmlWithAbsoluteImages]);

  const shareUrl = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const path =
      typeof window !== "undefined"
        ? window.location.pathname.replace(/\/index\.html$/, "").replace(/\/$/, "")
        : "";
    return `${origin}${path}/share.php?slug=${encodeURIComponent(postId)}`;
  }, [postId]);
  const shareSocials = useMemo(
    () => [
      {
        platform: "facebook",
        label: "Share on Facebook",
        iconClass: "facebook",
        url: shareUrl,
      },
      {
        platform: "instagram",
        label: "Copy link for Instagram",
        iconClass: "instagram",
        url: shareUrl,
      },
      {
        platform: "youtube",
        label: "Copy link for YouTube",
        iconClass: "youtube",
        url: shareUrl,
      },
      {
        platform: "tiktok",
        label: "Copy link for TikTok",
        iconClass: "tiktok",
        url: shareUrl,
      },
    ],
    [shareUrl],
  );

  const breadcrumbItems = useMemo(() => {
    const currentLabel = activePost?.title || "Article";
    return [
      { id: '/blog', label: "Blog" },
      { id: activePost?.id || postId || currentLabel, label: currentLabel },
    ];
  }, [activePost?.id, activePost?.title, postId]);

  const handleBreadcrumbNavigate = (target) => {
    if (typeof window === "undefined") return;
    if (!target) return;
    window.history.pushState({}, "", target);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const openShare = (platform) => (e) => {
    e.preventDefault();
    const url = encodeURIComponent(shareUrl);
    let href = "";
    switch (platform) {
      case "facebook":
        href = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case "youtube":
      case "instagram":
      case "tiktok":
        try {
          navigator.clipboard?.writeText(shareUrl);
          alert("Link copied to clipboard");
        } catch (_) {}
        return;
      default:
        break;
    }
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  if (loading && !activePost) {
    return (
      <div className="rm-page-loading">
        <Loader />
      </div>
    );
  }

  if (!activePost && !loading) {
    return (
      <div className={`${styles.page}`}>
        <main className={`${styles.main} grid`}>
          <SectionHeader
            title="Post not found"
            pageTitle
            variant="h2"
            showDivider={false}
          />
          <Button as="a" href="/blog" label="Back to Blog" primary />
        </main>
      </div>
    );
  }

  return (
    <div className={`${styles.page}`}>
      {activePost?.imgeUrl ? (
        <section className={styles.heroBanner} aria-label="Article hero image">
          <img
            src={toMediaUrl(activePost.imgeUrl)}
            alt={activePost?.title || "Article"}
            loading="eager"
          />
        </section>
      ) : null}
      <div className={`${styles.topRail} p-md`}>
        <div className={styles.topRailBreadcrumbs}>
          <Breadcrumb
            items={breadcrumbItems}
            separator="/"
            onNavigate={handleBreadcrumbNavigate}
            className={styles.breadcrumbs}
          />
        </div>
        <div className={styles.shareRow}>
          <ShareThisArticle
            layout="horizontal"
            title="Share this article:"
            socials={shareSocials}
            onShare={openShare}
          />
        </div>
      </div>
      <div className={`${styles.content} grid`}>
        <main className={styles.main}>
          <header className={styles.header}>
            <div className={`${styles.headerInner} article-body`}>
              <div className={styles.articleMeta}>
                <div className={styles.metaLeft}>
                  <span className={`${styles.eyebrow} t-caption`}>
                    {meta.eyebrow}
                  </span>
                  <span className={`${styles.metaText} t-caption`}>
                    {meta.metaText}
                  </span>
                </div>
              </div>
              <h1 className={`${styles.articleTitle} t-display-title`}>
                {activePost?.title || ""}
              </h1>
              {activePost?.subtitle ? (
                <p className={`${styles.subtitle} t-h4`}>
                  {activePost.subtitle}
                </p>
              ) : null}
            </div>
          </header>

          <section className={styles.articleBody}>
            <div
              ref={articleBodyRef}
              className={`${styles.articleBodyContent} t-body article-body`}
              dangerouslySetInnerHTML={{ __html: bodyHtmlWithAbsoluteImages }}
            />
          </section>
          {referencesHtml ? (
            <section className={`${styles.referencesSection} p-xl`}>
              <h3 className="t-h3">References</h3>
              <div
                className={`${styles.referencesContent} t-body article-body`}
                dangerouslySetInnerHTML={{ __html: referencesHtml }}
              />
            </section>
          ) : null}
          <div className={styles.pagination}>
            {prev ? (
              <Button
                as="a"
                href={`/blog/${prev.slug || prev.id}`}
                icon="arrow_back"
                label={prev.title}
                link
                size="lg"
              />
            ) : (
              <span />
            )}
            {next ? (
              <Button
                as="a"
                href={`/blog/${next.slug || next.id}`}
                icon="arrow_forward"
                label={next.title}
                link
                iconPosition="right"
                size="lg"
              />
            ) : (
              <span />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
